<template>
  <div id="story-board-content-print" class="page-no-break-before" :class="print_mode ? '' : 'vh-adjust'">
    <!-- 로딩 상태 -->
    <template v-if="disclosures === null">
      <div class="mb-5">
        <div>
          <span class="mt-5"></span>
          <h5 class="mb-4 border-bottom-dark pb-2">
            <b-skeleton animation="wave" width="15%"></b-skeleton>
            <b-skeleton animation="wave" width="30%"></b-skeleton>
            <span class="gray-500 f-105 fw-400 ml-1">
              <b-skeleton animation="wave" width="20%"></b-skeleton>
            </span>
          </h5>
        </div>

        <b-skeleton animation="fade" width="85%"></b-skeleton>
        <b-skeleton animation="fade" width="55%"></b-skeleton>
        <b-skeleton animation="fade" width="80%"></b-skeleton>
        <b-skeleton animation="fade" width="30%"></b-skeleton>
        <b-skeleton animation="fade" width="40%"></b-skeleton>
        <br />
        <b-skeleton animation="fade" width="40%"></b-skeleton>
        <b-skeleton animation="fade" width="20%"></b-skeleton>
      </div>
    </template>

    <!-- 공시 내용 -->
    <template v-else-if="disclosures.length > 0">
      <div v-for="(disclosure_group, group_idx) in groupedDisclosures" :key="'group_' + group_idx"
        :id="!print_mode && disclosure_group.type !== 'documents' ? 'order_id_' + disclosure_group.order_index : ''">
        <!-- 그룹화된 제목과 내용 렌더링 -->
        <template v-if="disclosure_group.grouped">
          <div class="grouped-title-content d-flex flex-wrap mt-5 w-100">
            <!-- 제목 렌더링 -->
            <Title v-if="lang === 'KOR'" :main_title="disclosure_group.title1"
              :title_lv="disclosure_group.title_lv" :link="disclosure_group.link" :print_mode="print_mode"
              :isFirstLv1="disclosure_group.title_lv === 1 && group_idx === 0"
              :prevTitleLv="groupedDisclosures[group_idx - 1]?.title_lv || null" class="col-2 pl-0" />

            <!-- 텍스트 또는 차트 렌더링 -->
            <div v-if="disclosure_group.content.type === 'text' && !disclosure_group.guide_mode" class="mt-0 col-10 no-page-break-inside text-box" :style="{ backgroundColor: disclosure_group.content.background_color }">
              <mdsb v-if="lang === 'KOR'" :contents="disclosure_group.content.text"
                
                 style="
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  white-space: normal;
                " />
                
              <mdsb v-else
                :contents="disclosure_group.content.text_em ? disclosure_group.content.text_em : disclosure_group.content.text_ea"
                style="
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  white-space: normal;
                " />
            </div>

            <template v-else-if="disclosure_group.content.type === 'chart'">
              <ChartCanvasSb :chart_id="disclosure_group?.content?.data?.id"
                :key="`chart_${disclosure_group?.content?.data?.id}`"
                :chartData="disclosure_group.content.data.contents"
                :chartData_e="disclosure_group.content.data.translated?.contents_e"
                class="col-10 no-page-break-inside text-box" />
            </template>

            <template v-else-if="disclosure_group.content.type === 'document'">
              <DocumentSb :print_mode="true" :document_info="disclosure_group.content.data"
                :document_id="disclosure_group?.content?.data?.id"
                class="col-10 no-page-break-inside" />
            </template>
          </div>
        </template>

        <!-- 일반적인 공시 그룹 렌더링 -->
        <template v-else>
          <!-- 문서 그룹 -->
          <div v-if="disclosure_group.type === 'documents'"
            class="documents-group d-flex flex-wrap mt-5 no-page-break-inside">
            <DocumentSb v-for="(doc) in disclosure_group.items" :print_mode="true" :key="'document_' + doc.index"
              :document_info="doc.data" :document_id="doc?.data?.id"
              class="document-item col-10 offset-2" />
          </div>

          <!-- 기타 타입 처리 -->
          <template v-else>
            <!-- 제목 -->
            <template v-if="disclosure_group.type === 'title'">
              <Title v-if="lang === 'KOR'" :main_title="disclosure_group?.title1"
                :title_lv="disclosure_group?.title_lv" :link="disclosure_group?.link" :print_mode="print_mode"
                :isFirstLv1="disclosure_group.title_lv === 1 && group_idx === 0"
                :prevTitleLv="groupedDisclosures[group_idx - 1]?.title_lv || null" />

              <Title v-else
                :main_title="disclosure_group?.title1_em ? disclosure_group?.title1_em : disclosure_group?.title1_ea"
                :title_lv="disclosure_group?.title_lv" :link="disclosure_group?.link" :print_mode="print_mode"
                :isFirstLv1="disclosure_group.title_lv === 1 && group_idx === 0"
                :prevTitleLv="groupedDisclosures[group_idx - 1]?.title_lv || null" />
            </template>

            <!-- 텍스트 또는 차트 (그룹화되지 않은 경우) -->
            <div v-else-if="disclosure_group.type === 'text' && !disclosure_group.guide_mode" class="mt-0 col-10 offset-2 text-box" :style="{ backgroundColor: disclosure_group.background_color }">
              <mdsb v-if="lang === 'KOR'" :contents="disclosure_group.text"
                style="
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  white-space: normal;
                " />
              <mdsb v-else
                :contents="disclosure_group.text_em ? disclosure_group.text_em : disclosure_group.text_ea" 
                style="
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  white-space: normal;
                " />
            </div>

            <div v-else-if="disclosure_group.type === 'chart'">
              <ChartCanvasSb class="col-10 offset-2 no-page-break-inside text-box"
                :chart_id="disclosure_group?.data?.id" :key="`chart_${disclosure_group?.data?.id}`" 
                :chartData="disclosure_group.data.contents"
                :chartData_e="disclosure_group.data.translated?.contents_e" />
            </div>


            <!-- 이미지 -->
            <div v-else-if="disclosure_group.type === 'image'" class="d-flex flex-column mt-5 no-page-break-inside text-box">
              <ImageContent :disclosure-info="disclosure_group" :lang="lang" :print_mode="print_mode" :show_title="disclosure_group.show_title"/>
            </div>

            <!-- 선 -->
            <div v-else-if="disclosure_group.type === 'line'">
              <hr :style="disclosure_group.hr_style" />
            </div>

            <!-- 데이터 테이블 -->
            <div v-else-if="disclosure_group.type === 'data_table'" class="mt-5 no-page-break-inside text-box">
              <DataTableSb :data_table_data="disclosure_group.data" :print_mode="print_mode" table_mode="accordion" :show_title="disclosure_group.show_title" :force_lang="lang"
                />
            </div>

            <!-- 비즈니스 케이스 -->
            <div v-else-if="disclosure_group.type === 'business_case'"
              class="mt-5 no-page-break-inside col-10 offset-2 text-box">
              <BusinessCaseSb :business_case_obj="disclosure_group.data" :business_case_id="disclosure_group?.data?.id"
                business_case_mode="accordion" :background_color="disclosure_group.background_color" :show_title="disclosure_group.show_title"/>
            </div>

            <!-- 단일 문서 -->
            <div v-else-if="disclosure_group.type === 'documents'" class="mt-3 col-10 offset-2">
              <DocumentSb :print_mode="true" :document_info="disclosure_group.data"
                :document_id="disclosure_group?.data?.id"/>
            </div>

            <div v-else-if="disclosure_group.type === 'document_set'" class="mt-3 col-10 offset-2 text-box">
              <DocumentSetSb :print_mode="true" :disclosure_group="disclosure_group" />
            </div>
          </template>
        </template>
      </div>
    </template>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import DocumentSb from './DocumentSb.vue';
import DataTableSb from './DataTableSb.vue';
import BusinessCaseSb from './BusinessCaseSb.vue';
import ChartCanvasSb from './ChartCanvasSb.vue';
import Title from './Title.vue';
import ImageContent from './ImageContent.vue';
import DocumentSetSb from './DocumentSetSb.vue'

export default {
  name: 'ContentPrint',
  props: {
    disclosures: { type: Array, default: null },
    print_mode: { type: Boolean, default: false },
  },
  components: {
    DocumentSb,
    DataTableSb,
    BusinessCaseSb,
    ChartCanvasSb,
    Title,
    ImageContent,
    DocumentSetSb,
  },
  watch: {
    disclosures: {
      immediate: true,
      handler(newV) {
        if (newV) {
          setTimeout(() => {
            this.moveScroll();
          }, 500);
        }
      },
    },
  },
  computed: {
    ...mapState('report_list', ['report_code', 'lang', 'view_mode']),

    // 그룹화된 공시 항목 계산 속성
    groupedDisclosures() {
      if (!this.disclosures) return [];

      const grouped = [];
      let i = 0;
      while (i < this.disclosures.length) {
        const current = this.disclosures[i];

        // 현재 항목이 제목이고 title_lv > 2이며 다음 항목이 텍스트 또는 차트인 경우 그룹화
        if (
          current.type === 'title' &&
          current.title_lv > 2 &&
          i + 1 < this.disclosures.length &&
          (this.disclosures[i + 1].type === 'text' ||
            this.disclosures[i + 1].type === 'chart' ||
            this.disclosures[i + 1].type === 'document')
        ) {
          const next = this.disclosures[i + 1];
          grouped.push({
            grouped: true,
            title1: current.title1,
            title2: current.title2,
            title_lv: current.title_lv,
            link: current.link,
            content: next,
          });
          i += 2; // 다음 항목은 이미 그룹화되었으므로 스킵
        } else if (current.type === 'document') {
          // 문서 그룹화
          const documents = [];
          while (i < this.disclosures.length && this.disclosures[i].type === 'document') {
            documents.push({ ...this.disclosures[i], index: i });
            i++;
          }
          grouped.push({ type: 'documents', items: documents });
        } else {
          // 일반적인 항목 추가
          grouped.push({ ...current, index: i });
          i++;
        }
      }

      return grouped;
    },
  },
  methods: {
    moveScroll() {
      const hash = this.$route.hash;
      if (hash) {
        setTimeout(() => {
          const targetElement = document.querySelector(hash);
          if (targetElement) {
            const elementPosition =
              targetElement.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - 90;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            });
          }
        }, 300);
      }
    },
  },
};
</script>

<style>
.documents-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  /* 문서 간 간격 조정 */
}

.document-item {
  flex: 1 1 calc(25% - 16px);
  /* 한 줄에 네 개의 문서 배치 */
  box-sizing: border-box;
}

@media (max-width: 1200px) {
  .document-item {
    flex: 1 1 calc(33.3% - 16px);
    /* 화면 폭이 줄어들면 세 개씩 배치 */
  }
}

@media (max-width: 576px) {
  .document-item {
    flex: 1 1 calc(50% - 16px);
    /* 모바일 등 작은 화면에서는 두 개씩 배치 */
  }
}

/* 그룹화된 제목과 내용 스타일 */
.grouped-title-content {
  width: 100%;
}

.grouped-title-content .flex-1 {
  flex: 1;
  margin-right: 16px;
}

.grouped-title-content .flex-2 {
  flex: 2;
}
</style>